<template>
  <PivotHeaderCell
    v-for="cell in headerCells"
    :key="cell.key"
    :class="cell.class"
    :style="cell.style"
    :text="cell.text"
    :desc="cell.desc"
    :tooltip="cell.tooltip"
    :allow-resize="cell.allowResize"
    :revealed="cell.revealed"
    :opened="cell.opened"
    :row="cell.row"
    :col="cell.col"
    :row-span="cell.rowSpan"
    :col-span="cell.colSpan"
    @resize="width => $emit('resize', cell.resizePath, width)"
    @reset="$emit('reset', cell.resizePath)"
    @toggle="handleToggleColumnGroup(cell.revealPath)"
  />
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import {
  PivotTableCaptions,
  PivotTableData,
  PivotTableTooltips,
  PivotTableValueDescriptions,
  PivotTableValueRenderers,
} from '@types'

import {
  CAPTIONS_PROVIDE_KEY,
  COL_GROUP_DEPTH_KEY,
  DISABLE_COLS_PROVIDE_KEY,
  GRAND_TOTAL_CAPTION,
  PATH_DELIMITER,
  RENDERERS_PROVIDE_KEY,
  TOOLTIPS_PROVIDE_KEY,
  VALUE_DESCRIPTIONS_PROVIDE_KEY,
} from '@/components/DS/patterns/Pivot/utils/const'
import { getColumnPathValues, renderCaption, renderValue } from '@/components/DS/patterns/Pivot/utils/query'

import PivotHeaderCell from './primitives/PivotHeaderCell.vue'

// Each header level is a sticky row stacked at `level * ROW_LEVEL_OFFSET`px.
const ROW_LEVEL_OFFSET = 35

type Props = {
  columns: PivotTableData[]
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
}

type Emits = {
  resize: [path: string[], width: number]
  reset: [path: string[]]
}

const { columns, colsList = [], rowsList = [], valuesList = [] } = defineProps<Props>()
defineEmits<Emits>()

const captions = inject(CAPTIONS_PROVIDE_KEY, computed<PivotTableCaptions>(() => ({})))
const renderers = inject(RENDERERS_PROVIDE_KEY, computed<PivotTableValueRenderers>(() => ({})))
const valueDescriptions = inject(VALUE_DESCRIPTIONS_PROVIDE_KEY, computed<PivotTableValueDescriptions>(() => ({})))
const tooltips = inject(TOOLTIPS_PROVIDE_KEY, computed<PivotTableTooltips>(() => ({})))
const disableCols = inject(DISABLE_COLS_PROVIDE_KEY, false)
const toggleColumnGroup = inject<(path: string) => void>('toggleColumnGroup')

const handleToggleColumnGroup = (path?: string) => {
  if (!path) return
  toggleColumnGroup?.(path)
}

type HeaderCellSpec = {
  key: string
  text: string
  desc?: string
  tooltip?: string
  class?: string
  style?: Record<string, string>
  allowResize: boolean
  resizePath: string[]
  /** Column-group collapse toggle — undefined for cells with no collapse concept (grand total, row label). */
  revealed?: boolean
  opened?: boolean
  revealPath?: string
  /** Negative — header rows count up to -1 immediately above the body. See `usePivotSelection`. */
  row: number
  /** 0-based leaf-column position, left to right. */
  col: number
  rowSpan?: number
  colSpan?: number
}

const colsCount = computed(() => colsList.length)
const valuesCount = computed(() => valuesList.length)
const lastValueField = computed(() => valuesList.at(-1) ?? GRAND_TOTAL_CAPTION)

// How many column-group levels render: one per `colsList` field, or a single grand-total
// stand-in, or none — mirrors `columnCells`'s branching below.
const columnLevelCount = computed(() => {
  if (colsCount.value && valuesCount.value) return colsList.length
  return valuesCount.value && !disableCols ? 1 : 0
})
// Mirrors `valueCells`'s own early-return condition. Needed to tell value fields apart under each
// column group (`valuesCount > 1`), or to stand in for column headers when there are none
// (`disableCols`) — with a single value field and real column headers, the column header alone
// already says what's in each cell, so this extra level would be redundant.
const hasValueCellsRow = computed(() => valuesCount.value > 1 || disableCols)

// Total header rows above the body — the row-label corner spans all of them; every other header
// row is a negative offset from this count (see `usePivotSelection`).
const totalHeaderLevels = computed(() => columnLevelCount.value + (hasValueCellsRow.value ? 1 : 0))
// Row-label column(s) come first in the grid; every other header cell's `col` starts after them.
const rowLabelColCount = computed(() => rowsList.length)

// If all columns are collapsed above a level, it has no visible cells and its row shrinks to 0px.
// Only levels with real cells add ROW_LEVEL_OFFSET; hidden levels add 0.
const levelTopOffsets = computed(() => {
  const offsets: number[] = []
  let offset = 0
  for (const key of colsList) {
    offsets.push(offset)
    if (columns.some(column => column[key] !== undefined)) offset += ROW_LEVEL_OFFSET
  }
  offsets.push(offset)
  return offsets
})

// The row-label header cell: bold, pinned to the top-left corner.
const ROW_CLASS = 'min-h-8 justify-start text-left bg-card left-0 font-bold text-xs tracking-ds-caps flex-auto uppercase z-20'
// Column-group header levels — all sit on the same plain card background; only text
// weight/case/size (set per level below in `columnCells`/`valueCells`) carries the hierarchy.
const LEVEL_CLASS: Record<number, string> = {
  0: 'left-0 font-bold text-xs tracking-ds-caps flex-auto z-10 [&_[data-text="true"]]:uppercase',
}
const levelClass = (level: number) => LEVEL_CLASS[level] ?? ''
// The value-field caption row ("Value", "Gain"): bold like a corner header when it's
// the only header row (no column grouping above it); muted and de-emphasized when it sits under
// column-group bands, where its job is mostly to preview each column's own total (see
// `footerValuesByColumn`) rather than to read as its own heading.
const FLAT_VALUE_CLASS = 'font-bold text-xs tracking-ds-caps'
const DENSE_VALUE_CLASS = 'text-muted-foreground font-semibold'

const SINGLE_HEADER_LEVEL_CLASS = ' min-h-14 border-r-transparent! -ml-px [&_[data-resizer="true"]]:justify-end'
const isSingleHeaderLevel = computed(() => disableCols || (columnLevelCount.value <= 1 && valuesCount.value <= 1))
const singleHeaderLevelClass = computed(() => isSingleHeaderLevel.value ? SINGLE_HEADER_LEVEL_CLASS : '')

// One cell per row field, spanning every column-header level (row-label is a single sticky-left strip).
const rowCells = computed<HeaderCellSpec[]>(() => {
  return rowsList.map((row, index) => ({
    key: `row:${row}`,
    text: renderCaption(row, captions.value),
    tooltip: tooltips.value?.[row],
    class: `${ROW_CLASS}${singleHeaderLevelClass.value}`,
    style: totalHeaderLevels.value > 1 ? { gridRow: `span ${totalHeaderLevels.value}` } : undefined,
    allowResize: true,
    resizePath: [row],
    row: -totalHeaderLevels.value,
    col: index,
    rowSpan: totalHeaderLevels.value,
  }))
})

// Column-group headers: one level per enabled `cols` field, spanning `colSpan * valuesCount`
// columns. A trailing "Grand total" cell closes out level 0 when there's >1 value field.
const columnCells = computed<HeaderCellSpec[]>(() => {
  if (!colsCount.value || !valuesCount.value) {
    if (valuesCount.value && !disableCols) {
      return [{
        key: 'grand-total',
        text: GRAND_TOTAL_CAPTION,
        desc: valueDescriptions.value?.['grand-total'],
        class: `${levelClass(0)}${singleHeaderLevelClass.value}`,
        style: { gridColumn: `span ${valuesCount.value}`, top: '0px' },
        allowResize: valuesCount.value < 2,
        resizePath: [lastValueField.value],
        row: -totalHeaderLevels.value,
        col: rowLabelColCount.value,
        colSpan: valuesCount.value,
      }]
    }
    return []
  }

  const cells: HeaderCellSpec[] = []
  const maxColSpan: number[] = []
  colsList.forEach((key, level) => {
    let colSpan = 1
    const isLastLevel = level === colsList.length - 1
    // Only deepest level is resizable
    const allowResize = valuesCount.value < 2 && isLastLevel
    for (let index = 0; index < columns.length; index++) {
      const column = columns[index]
      const value = column[key]
      if (
        value !== undefined &&
        columns[index + 1]?.[key] === value &&
        (!maxColSpan[index] || colSpan < maxColSpan[index] - index)
      ) {
        colSpan++
        continue
      }
      if (value === undefined) {
        // Hidden: covered by ancestor's spanning header cell
        maxColSpan[index] = colSpan
        colSpan = 1
        continue
      }
      const path = getColumnPathValues(column)
      if (valuesCount.value > 1) path.push(lastValueField.value)
      const groupDepth = column[COL_GROUP_DEPTH_KEY] as number | undefined
      const isCollapsedHere = groupDepth === level
      // The span just ended at `index` — it started `colSpan` entries back.
      const startIndex = index - colSpan + 1
      cells.push({
        key: `col:${level}:${index}`,
        text: renderValue(column[key], key, renderers.value, 'header'),
        class: `${levelClass(level)}${singleHeaderLevelClass.value}${isLastLevel && valuesCount.value <= 1 ? ' justify-end text-right' : ''}`,
        style: {
          gridColumn: `span ${colSpan * valuesCount.value}`,
          top: `${levelTopOffsets.value[level]}px`,
          // Collapsed group: span child header rows
          ...(isCollapsedHere ? { gridRow: `span ${colsList.length - level}` } : {}),
        },
        allowResize,
        resizePath: path,
        revealed: !isLastLevel,
        opened: !isCollapsedHere,
        revealPath: colsList.slice(0, level + 1).map(k => column[k]).join(PATH_DELIMITER),
        row: level - totalHeaderLevels.value,
        col: rowLabelColCount.value + startIndex * valuesCount.value,
        colSpan: colSpan * valuesCount.value,
        rowSpan: isCollapsedHere ? colsList.length - level : undefined,
      })
      maxColSpan[index] = colSpan
      colSpan = 1
    }
    if (level === 0 && valuesCount.value) {
      cells.push({
        key: 'grand-total',
        text: GRAND_TOTAL_CAPTION,
        desc: valueDescriptions.value?.['grand-total'],
        class: `${levelClass(0)}${isSingleHeaderLevel.value ? '' : ' bg-accent'}${singleHeaderLevelClass.value}${isLastLevel ? ' justify-end text-right' : ''}`,
        style: { gridColumn: `span ${colSpan * valuesCount.value}`, gridRow: `span ${colSpan * colsCount.value}`, top: '0px' },
        allowResize: valuesCount.value < 2,
        resizePath: [lastValueField.value],
        row: -totalHeaderLevels.value,
        col: rowLabelColCount.value + columns.length * valuesCount.value,
        colSpan: colSpan * valuesCount.value,
        rowSpan: colSpan * colsCount.value,
      })
    }
  })
  return cells
})

// Sub-header row of value captions under each column group — needed when there's more than one
// value field, columns are disabled (then this row stands in for them), or there's a column
// grouping at all (then it also previews each column's own total — see `footerValuesByColumn`).
const valueCells = computed<HeaderCellSpec[]>(() => {
  if (!hasValueCellsRow.value) return []
  const valueLevel = colsCount.value || disableCols ? colsList.length : 1
  const cells: HeaderCellSpec[] = []
  for (let i = 0; i < columns.length + 1; i++) {
    const path = columns[i] ? getColumnPathValues(columns[i]) : []
    valuesList.forEach((value, valueIndex) => {
      cells.push({
        key: `value:${i}:${value}`,
        text: renderCaption(value, captions.value),
        desc: valueDescriptions.value?.[value],
        tooltip: tooltips.value?.[value],
        class: `${colsCount.value > 0 ? DENSE_VALUE_CLASS : FLAT_VALUE_CLASS} justify-end font-semibold text-right${singleHeaderLevelClass.value} [&_[data-text="true"]]:uppercase`,
        style: { top: `${levelTopOffsets.value[valueLevel] ?? ROW_LEVEL_OFFSET * valueLevel}px` },
        allowResize: true,
        resizePath: [...path, value],
        row: -1,
        col: rowLabelColCount.value + i * valuesCount.value + valueIndex,
      })
    })
  }
  return cells
})

const headerCells = computed(() => [...rowCells.value, ...columnCells.value, ...valueCells.value])
</script>
